import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { 
  Shield, 
  CheckCircle, 
  RefreshCw, 
  Headphones, 
  Clock, 
  Award,
  Star,
  ThumbsUp,
  ArrowRight,
  Phone,
  BadgeCheck,
  Heart,
  Zap,
  Lock,
  Send,
  AlertCircle,
  FileText,
  Scale
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GarantiPage = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    service_type: '',
    request_number: '',
    problem_category: '',
    problem_detail: '',
    incident_date: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const problemCategories = [
    'Ustanın üslubu ve tarzı kötüydü',
    'Beni tehdit etti',
    'Paramı aldı ama işe başlamadı',
    'İşe gelmedi / geç geldi',
    'Anlaştığımdan başka biri geldi',
    'İşi kötü yaptı',
    'Anlaştığım fiyattan fazla istedi',
    'Bazı eşyalarımı bulamıyorum',
    'İşimi yarım bıraktı',
    'Hizmet veren işi yaparken etrafa zarar verdi',
    'Başka bir sorunum var'
  ];

  const serviceTypes = [
    'Kombi Servisi',
    'Klima Servisi',
    'Beyaz Eşya Tamiri',
    'Elektrik İşleri',
    'Su Tesisatı',
    'Diğer'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/guarantee-request`, {
        customer_name: formData.full_name,
        customer_phone: formData.phone,
        customer_email: formData.email,
        service_type: formData.service_type,
        service_date: formData.incident_date,
        issue_description: `${formData.problem_category}: ${formData.problem_detail}`,
        request_id: formData.request_number
      });
      if (response.data.success) {
        setFormSuccess(true);
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          service_type: '',
          request_number: '',
          problem_category: '',
          problem_detail: '',
          incident_date: ''
        });
      }
    } catch (error) {
      setFormError('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setFormLoading(false);
    }
  };

  const guarantees = [
    {
      icon: Shield,
      title: 'Hizmet Garantisi',
      description: 'Tüm hizmetlerimiz 30 gün boyunca garantili. Aynı sorun tekrar ederse ücretsiz onarım.',
      color: 'from-[#006D77] to-[#0A9396]'
    },
    {
      icon: RefreshCw,
      title: 'Para İade Garantisi',
      description: 'Hizmetten memnun kalmazsanız, ödemenizi 7 gün içinde iade ediyoruz.',
      color: 'from-[#E29578] to-[#D67A56]'
    },
    {
      icon: BadgeCheck,
      title: 'Onaylı Ustalar',
      description: 'Tüm ustalarımız kimlik ve yetkinlik kontrolünden geçirilmiştir.',
      color: 'from-[#059669] to-[#047857]'
    },
    {
      icon: Lock,
      title: 'Güvenli Ödeme',
      description: 'Ödemeleriniz güvende. İş tamamlanmadan usta ödeme almaz.',
      color: 'from-[#7C3AED] to-[#6D28D9]'
    },
    {
      icon: Headphones,
      title: '7/24 Destek',
      description: 'Herhangi bir sorun yaşarsanız, müşteri hizmetlerimiz her zaman yanınızda.',
      color: 'from-[#2563EB] to-[#1D4ED8]'
    },
    {
      icon: Clock,
      title: 'Zamanında Hizmet',
      description: 'Randevu saatine uyulmadığında, bir sonraki hizmette %20 indirim.',
      color: 'from-[#DC2626] to-[#B91C1C]'
    }
  ];

  const steps = [
    {
      step: '01',
      title: 'Sorun Bildir',
      desc: 'Hizmetten memnun değilseniz 48 saat içinde bize bildirin.'
    },
    {
      step: '02', 
      title: 'İnceleme',
      desc: 'Ekibimiz durumu değerlendirir ve en iyi çözümü sunar.'
    },
    {
      step: '03',
      title: 'Çözüm',
      desc: 'Ücretsiz onarım, yeniden hizmet veya para iadesi sağlanır.'
    }
  ];

  const stats = [
    { value: '99%', label: 'Müşteri Memnuniyeti' },
    { value: '24 Saat', label: 'Ortalama Çözüm Süresi' },
    { value: '10.000+', label: 'Çözülen Talep' },
    { value: '4.9/5', label: 'Garanti Puanı' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#006D77] via-[#0A9396] to-[#006D77] overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2.5 mb-6">
              <Shield className="w-5 h-5 text-white" />
              <span className="text-white font-semibold">HizmetGeliyo Garantisi</span>
            </div>

            {/* Main Title */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Memnuniyetiniz,<br />
              <span className="text-[#F6AD55]">Bizim Taahhüdümüz</span>
            </h1>

            <p className="text-white/80 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
              Her hizmet, garantimiz altında. Sorun yaşarsanız çözüm bizden. 
              Memnun kalmazsanız paranız iade.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/20">
                  <div className="text-2xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-white/70 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guarantees Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Garantilerimiz
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Her hizmette altı farklı garanti ile yanınızdayız
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guarantees.map((guarantee, index) => {
              const Icon = guarantee.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${guarantee.color} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{guarantee.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{guarantee.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Garanti Nasıl İşler?
            </h2>
            <p className="text-gray-600 text-lg">
              3 basit adımda sorunlarınızı çözüyoruz
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-[#006D77] via-[#E29578] to-[#059669]"></div>
            
            {steps.map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10 shadow-lg border-4 border-white">
                  <span className="text-4xl font-bold bg-gradient-to-br from-[#006D77] to-[#0A9396] bg-clip-text text-transparent">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
        {/* Decorative */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#006D77]/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#E29578]/20 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#006D77]/20 rounded-full px-4 py-2 mb-6">
                <Heart className="w-4 h-4 text-[#E29578]" />
                <span className="text-[#E29578] text-sm font-semibold">Müşteri Öncelikli</span>
              </div>
              
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-6 leading-tight">
                Sizin Memnuniyetiniz,<br />
                <span className="text-[#F6AD55]">Bizim Başarımız</span>
              </h2>
              
              <p className="text-white/70 text-lg mb-8 leading-relaxed">
                HizmetGeliyo olarak tek amacımız sizin memnuniyetiniz. Her hizmet, 
                her usta, her iş bizim için önemli. Sorun yaşarsanız, çözüm için 
                elimizden geleni yapıyoruz.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-white/90">Tüm ustalar kimlik doğrulamalı</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-white/90">Hizmet kalitesi sürekli denetleniyor</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-white/90">Müşteri şikayetleri 24 saat içinde yanıtlanır</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-white/90">Memnuniyet garantisi her hizmette geçerli</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-2xl flex items-center justify-center">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">%100 Koruma</h3>
                    <p className="text-white/60 text-sm">Tüm hizmetlerde geçerli</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/80 text-sm">Hizmet Garantisi</span>
                      <span className="text-green-400 text-sm font-semibold">30 Gün</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2">
                      <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full w-full"></div>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/80 text-sm">Para İade Süresi</span>
                      <span className="text-blue-400 text-sm font-semibold">7 Gün</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2">
                      <div className="bg-gradient-to-r from-blue-400 to-indigo-500 h-2 rounded-full w-[70%]"></div>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/80 text-sm">Destek Erişimi</span>
                      <span className="text-purple-400 text-sm font-semibold">7/24</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2">
                      <div className="bg-gradient-to-r from-purple-400 to-violet-500 h-2 rounded-full w-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Form Section - Inline */}
      <section id="sorun-bildir" className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#E29578]/10 border border-[#E29578]/30 rounded-full px-5 py-2.5 mb-6">
              <Headphones className="w-5 h-5 text-[#E29578]" />
              <span className="text-[#E29578] font-semibold">Destek Merkezi</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Sorun mu Yaşadınız?
            </h2>
            <p className="text-gray-600 text-lg max-w-xl mx-auto">
              Hizmetinizle ilgili bir problem mi var? Formu doldurun, 48 saat içinde size ulaşalım.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden" data-testid="support-form-section">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#006D77] to-[#0A9396] p-6 md:p-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Headphones className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-white">Destek Talebi Oluştur</h3>
                  <p className="text-white/70 text-sm md:text-base">Sorununuzu detaylı anlatın, hemen çözüm üretelim.</p>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 md:p-8">
              {formSuccess ? (
                <div className="text-center py-12" data-testid="support-form-success">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Talebiniz Alındı!</h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    Destek talebiniz başarıyla iletildi. Ekibimiz en kısa sürede sizinle iletişime geçecektir.
                  </p>
                  <Button
                    onClick={() => setFormSuccess(false)}
                    className="bg-[#006D77] hover:bg-[#005a63] text-white px-8 py-3 rounded-xl"
                    data-testid="new-support-request-btn"
                  >
                    Yeni Talep Oluştur
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="support-form">
                  {formError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-red-600">{formError}</p>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Adınız Soyadınız <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        required
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        className="rounded-xl border-gray-200 focus:border-[#006D77] focus:ring-[#006D77] h-12"
                        placeholder="Ahmet Yılmaz"
                        data-testid="support-full-name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        E-posta <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="rounded-xl border-gray-200 focus:border-[#006D77] focus:ring-[#006D77] h-12"
                        placeholder="ornek@email.com"
                        data-testid="support-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Telefon
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="rounded-xl border-gray-200 focus:border-[#006D77] focus:ring-[#006D77] h-12"
                        placeholder="05XX XXX XX XX"
                        data-testid="support-phone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Hizmet Türü <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.service_type}
                        onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                        className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#006D77] focus:ring-[#006D77] h-12"
                        data-testid="support-service-type"
                      >
                        <option value="">Seçiniz...</option>
                        {serviceTypes.map((type, i) => (
                          <option key={i} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Talep Numarası
                      </label>
                      <Input
                        type="text"
                        value={formData.request_number}
                        onChange={(e) => setFormData({...formData, request_number: e.target.value})}
                        className="rounded-xl border-gray-200 focus:border-[#006D77] focus:ring-[#006D77] h-12"
                        placeholder="Varsa talep numaranız"
                        data-testid="support-request-number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Olay Tarihi
                      </label>
                      <Input
                        type="date"
                        value={formData.incident_date}
                        onChange={(e) => setFormData({...formData, incident_date: e.target.value})}
                        className="rounded-xl border-gray-200 focus:border-[#006D77] focus:ring-[#006D77] h-12"
                        data-testid="support-incident-date"
                      />
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sorun Kategorisi <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.problem_category}
                      onChange={(e) => setFormData({...formData, problem_category: e.target.value})}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#006D77] focus:ring-[#006D77] h-12"
                      data-testid="support-problem-category"
                    >
                      <option value="">Seçiniz...</option>
                      {problemCategories.map((cat, i) => (
                        <option key={i} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Detaylı Açıklama <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={formData.problem_detail}
                      onChange={(e) => setFormData({...formData, problem_detail: e.target.value})}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm resize-none focus:border-[#006D77] focus:ring-[#006D77]"
                      placeholder="Yaşadığınız sorunu mümkün olduğunca detaylı anlatın. Ne oldu? Ne zaman oldu? Usta nasıl davrandı?"
                      data-testid="support-problem-detail"
                    />
                  </div>

                  <div className="mt-8">
                    <Button
                      type="submit"
                      disabled={formLoading}
                      className="w-full bg-gradient-to-r from-[#006D77] to-[#0A9396] hover:from-[#005a63] hover:to-[#088488] text-white py-4 rounded-xl text-lg font-semibold shadow-lg"
                      data-testid="support-submit-btn"
                    >
                      {formLoading ? (
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Gönderiliyor...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-3">
                          <Send className="w-5 h-5" />
                          <span>Destek Talebi Gönder</span>
                        </div>
                      )}
                    </Button>
                  </div>

                  <p className="text-center text-gray-500 text-sm mt-4">
                    Talebiniz en geç 48 saat içinde değerlendirilecektir.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Legal Terms Link Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-200 p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-2xl flex items-center justify-center flex-shrink-0">
                <Scale className="w-10 h-10 text-white" />
              </div>
              <div className="text-center md:text-left flex-1">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  Garanti Şart ve Koşulları
                </h3>
                <p className="text-gray-600 mb-4">
                  HizmetGeliyo garantisinin tüm yasal şartlarını ve koşullarını detaylı olarak inceleyin. 
                  Hangi durumlarda garantiden yararlanabileceğinizi öğrenin.
                </p>
                <Link 
                  to="/garanti-sartlari"
                  className="inline-flex items-center gap-2 text-[#006D77] hover:text-[#005a63] font-semibold transition-colors"
                  data-testid="legal-terms-link"
                >
                  <FileText className="w-5 h-5" />
                  Şartları ve Koşulları Oku
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Zap className="w-12 h-12 text-[#E29578] mx-auto mb-4" />
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            Güvenle Hizmet Alın
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-xl mx-auto">
            HizmetGeliyo garantisi ile her hizmet güvence altında. 
            Hemen başlayın, farkı yaşayın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/hizmetler')}
              className="bg-[#006D77] hover:bg-[#005a63] text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg"
              data-testid="get-service-btn"
            >
              Hizmet Al
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <a 
              href="tel:08502427808" 
              className="inline-flex items-center justify-center gap-2 border-2 border-[#006D77] text-[#006D77] hover:bg-[#006D77] hover:text-white px-8 py-4 rounded-full text-lg font-semibold transition-all"
              data-testid="call-support-btn"
            >
              <Phone className="w-5 h-5" />
              0850 242 78 08
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GarantiPage;
